/**
 * Status — единственный экран заявки. Пока awaiting_payment: карточка «К оплате» + выпадающий
 * список банка-отправителя (кастомный: лого + комиссия под названием, данные из banksApi) →
 * реквизиты и комиссия банка показываются только ПОСЛЕ выбора банка (чтобы клиент прочитал
 * комиссию; она справочная, в сумму не входит) + кнопка «Я оплатил». Если справочник банков
 * пуст/недоступен — гейт снимается, реквизиты показываются сразу. После нажатия — блок
 * «Проверяем оплату» (гейт по payment_claimed_at, бэк идемпотентен). Дальше — таймлайн
 * статусов. Live-поллинг. Пропсы: order, live, onDone.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { Card, FallbackButton, Row, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Button } from "@/components/Button";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { CopyIcon, useCopy } from "@/components/copy";
import { RequisitesBlock } from "@/components/RequisitesBlock";
import { banksApi, ordersApi, type ApiOrder, type OrderStatusApi, type PayerBank } from "@/lib/api";
import { formatRub } from "@/lib/format";
import { type KycState } from "@/screens/Profile";

type Props = {
  order: ApiOrder;
  live: boolean;
  onDone: () => void;
  kyc?: KycState;          // статус верификации — для подсказки на awaiting_kyc
  onVerify?: () => void;   // переход к верификации из экрана заявки
};

const STEPS: { key: OrderStatusApi; label: string }[] = [
  { key: "awaiting_kyc", label: "Проверка документов" },
  { key: "awaiting_payment", label: "Ожидаем оплату" },
  { key: "processing", label: "Оплата получена" },
  { key: "paid", label: "Зачисляем на счёт" },
  { key: "completed", label: "Счёт пополнен" },
];

const STATUS_IDX: Record<OrderStatusApi, number> = {
  awaiting_kyc: 0,
  awaiting_payment: 1,
  processing: 2,
  paid: 3,
  sending: 3,
  send_failed: 3,
  completed: 4,
  rejected: 1,
  cancelled: 1,
};

function isTerminal(s: OrderStatusApi): boolean {
  return s === "completed" || s === "rejected" || s === "cancelled";
}

const CANCELLABLE: OrderStatusApi[] = ["awaiting_kyc", "awaiting_payment"];

const isImage = (v: string) => /^(data:|https?:)/i.test(v);

function feeText(feePct: number): string {
  return feePct ? `комиссия банка ${feePct}%` : "без комиссии";
}

function BankLogo({ logo, size = 28 }: { logo: string | null; size?: number }) {
  return (
    <span
      className="grid shrink-0 place-items-center overflow-hidden rounded-lg bg-white/[0.06]"
      style={{ width: size, height: size }}
    >
      {logo && isImage(logo) ? (
        <img src={logo} alt="" className="h-full w-full object-cover" />
      ) : (
        <span style={{ fontSize: size * 0.62 }}>{logo || "🏦"}</span>
      )}
    </span>
  );
}

function SelectChevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      className={`h-5 w-5 shrink-0 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Выпадающий список банков-отправителей: логотип слева, комиссия под названием.
// Кастомный (нативный <select> не умеет картинку-лого и вторую строку).
function BankSelect({
  banks,
  value,
  onChange,
}: {
  banks: PayerBank[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = banks.find((b) => b.id === value) || null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-2xl border bg-surface-card px-4 py-3 text-left active:bg-white/[0.03] ${
          selected ? "border-white/[0.06]" : "border-brand/70 ring-1 ring-brand/20"
        }`}
      >
        {selected ? (
          <>
            <BankLogo logo={selected.logo} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-medium">{selected.name}</span>
              <span className="block text-[12px] text-white/45">{feeText(selected.fee_pct)}</span>
              {selected.comment && <span className="block text-[12px] text-white/45">{selected.comment}</span>}
            </span>
          </>
        ) : (
          <span className="flex-1 text-[15px] text-white/55">Выберите банк</span>
        )}
        <SelectChevron open={open} />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-surface-raised shadow-2xl shadow-black/50">
          {banks.map((b, i) => (
            <button
              key={b.id}
              onClick={() => {
                onChange(b.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left active:bg-white/[0.05] ${
                i > 0 ? "border-t border-white/[0.05]" : ""
              } ${b.id === value ? "bg-white/[0.04]" : ""}`}
            >
              <BankLogo logo={b.logo} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px]">{b.name}</span>
                <span className="block text-[12px] text-white/45">{feeText(b.fee_pct)}</span>
                {b.comment && <span className="block text-[12px] text-white/45">{b.comment}</span>}
              </span>
              {b.id === value && (
                <svg viewBox="0 0 20 20" className="h-5 w-5 shrink-0 text-brand" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M5 10.5l3.5 3.5L15 6.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Информация о комиссии выбранного банка — нейтральный блок, всегда после выбора
// (даже при нулевой комиссии пишем «без комиссии», а не прячем).
function CommissionNote({ bank }: { bank: PayerBank }) {
  return (
    <Note>
      Комиссия банка «{bank.name}»: {bank.fee_pct ? `${bank.fee_pct}%` : "без комиссии"}.
    </Note>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-[13px] font-medium text-white/55">{title}</div>
      {children}
    </div>
  );
}

export function Status({ order, live, onDone, kyc, onVerify }: Props) {
  const [current, setCurrent] = useState<ApiOrder>(order);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [claimingPay, setClaimingPay] = useState(false);
  const [payerBank, setPayerBank] = useState("");
  const [banks, setBanks] = useState<PayerBank[]>([]);
  const amountCopy = useCopy(String(current.amount_rub));

  useEffect(() => {
    banksApi.list().then(setBanks).catch(() => setBanks([]));
  }, []);

  useEffect(() => {
    if (!live || isTerminal(order.status)) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      try {
        const fresh = await ordersApi.get(order.id);
        if (!alive) return;
        setCurrent(fresh);
        if (isTerminal(fresh.status)) return;
      } catch {
        /* retry */
      }
      if (alive) timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [order.id, order.status, live]);

  const done = current.status === "completed";
  const rejected = current.status === "rejected";
  const cancelled = current.status === "cancelled";
  const terminal = rejected || cancelled;
  const canCancel = CANCELLABLE.includes(current.status);
  const currentIdx = STATUS_IDX[current.status];

  const awaitingPay = current.status === "awaiting_payment";
  const hasRequisites = awaitingPay && !!current.requisites;
  const claimed = !!current.payment_claimed_at;
  const showPay = hasRequisites && !claimed;
  const showClaimed = awaitingPay && claimed;
  const showTimeline = !terminal && !showPay;
  // Гейт реквизитов банком-отправителем: если банки заданы — обязателен выбор; если список
  // пуст (справочник не заполнен/недоступен) — не блокируем оплату, показываем реквизиты.
  const selectedBank = banks.find((b) => b.id === payerBank) || null;
  const bankGatePassed = banks.length === 0 || !!payerBank;

  const handlePaid = useCallback(async () => {
    if (claimingPay) return;
    setClaimingPay(true);
    try {
      setCurrent(await ordersApi.confirmPayment(current.id));
    } catch {
      /* не блокируем — статус подтянется поллингом */
    } finally {
      setClaimingPay(false);
    }
  }, [claimingPay, current.id]);

  const doCancel = async () => {
    setCancelling(true);
    try {
      setCurrent(await ordersApi.cancel(current.id));
      setConfirming(false);
    } catch {
      /* оставляем заявку как есть */
    } finally {
      setCancelling(false);
    }
  };

  const createdAt = new Date(current.created_at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  useBackButton(onDone);
  useMainButton({ text: done ? "Готово" : "В кабинет", onClick: onDone, active: true });

  const title = cancelled
    ? "Заявка отменена"
    : rejected
    ? "Заявка отклонена"
    : done
    ? "Счёт пополнен"
    : showPay
    ? "Оплатите заявку"
    : showClaimed
    ? "Проверяем оплату"
    : current.status === "awaiting_kyc"
    ? "Заявка создана"
    : "Обработка заявки";

  const heroIcon = cancelled ? "🚫" : rejected ? "❌" : done ? "🎉" : showPay ? "💳" : current.status === "awaiting_kyc" ? "⏳" : "🔄";

  const heroSubtitle = terminal
    ? "Вы можете создать новую заявку"
    : done
    ? "Средства зачислены на счёт"
    : showPay
    ? "Переведите точную сумму по реквизитам ниже"
    : showClaimed
    ? "Ждём подтверждения зачисления"
    : current.status === "awaiting_kyc"
    ? kyc === "pending"
      ? "Реквизиты появятся после проверки документов"
      : "Пройдите верификацию, чтобы получить реквизиты"
    : "Обрабатываем вашу заявку";

  return (
    <Screen>
      <Hero icon={heroIcon} title={title} subtitle={heroSubtitle} />

      <div className="space-y-5">
        {showPay && (
          <div className="rounded-3xl border border-brand/20 bg-brand/[0.07] p-5">
            <div className="text-[13px] text-white/55">К оплате</div>
            <div className="mt-1 flex items-center justify-between gap-3">
              <span className="text-[28px] font-semibold tracking-tight">{formatRub(current.amount_rub)}</span>
              <button
                onClick={amountCopy.copy}
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[12px] active:opacity-70"
              >
                {amountCopy.copied ? (
                  <span className="text-emerald-300">Скопировано ✓</span>
                ) : (
                  <>
                    <CopyIcon className="h-3.5 w-3.5" /> Сумма
                  </>
                )}
              </button>
            </div>
            <div className="mt-1 text-[13px] text-white/45">Зачислится на ваш рублёвый счёт</div>
          </div>
        )}

        {showPay && banks.length > 0 && (
          <Section title="Банк, с которого переводите">
            <BankSelect banks={banks} value={payerBank} onChange={setPayerBank} />
          </Section>
        )}

        {showPay && banks.length > 0 && !payerBank && (
          <Note>Выберите банк, с которого будете переводить — после этого покажем реквизиты.</Note>
        )}

        {showPay && selectedBank && <CommissionNote bank={selectedBank} />}

        {showPay && bankGatePassed && <RequisitesBlock order={current} />}

        {showPay && bankGatePassed && (
          <Button onClick={handlePaid} disabled={claimingPay}>
            {claimingPay ? "Отправляем…" : "Я оплатил"}
          </Button>
        )}

        {showClaimed && (
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-6 text-center">
            <Spinner className="mx-auto mb-3 h-8 w-8" />
            <div className="text-[15px] font-medium">Проверяем оплату</div>
            <div className="mx-auto mt-1 max-w-[17rem] text-[13px] leading-snug text-white/50">
              Мы получили информацию и проверяем зачисление. При поступлении платежа вам поступит
              уведомление и обновится баланс в приложении. Окно можно закрыть.
            </div>
          </div>
        )}

        {current.status === "awaiting_kyc" && kyc === "pending" && (
          <Note variant="warning">
            ⏳ Документы на проверке. Реквизиты для оплаты появятся здесь после проверки — обычно это
            занимает немного времени.
          </Note>
        )}

        {current.status === "awaiting_kyc" && kyc !== "pending" && (
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-5">
            <div className="text-[15px] font-medium">Нужна верификация</div>
            <div className="mt-1 text-[13px] leading-snug text-white/55">
              {kyc === "rejected"
                ? "Верификация отклонена. Пройдите её заново — после проверки здесь появятся реквизиты для оплаты."
                : "Чтобы получить реквизиты для оплаты, пройдите верификацию личности. Реквизиты появятся здесь автоматически после её прохождения."}
            </div>
            {onVerify && (
              <Button className="mt-3" onClick={onVerify}>
                Пройти верификацию
              </Button>
            )}
          </div>
        )}

        {terminal &&
          (cancelled ? (
            <Card>
              <div className="text-[15px] font-medium">Заявка отменена</div>
              <div className="mt-2 text-[13px] text-white/45">
                Вы можете создать новую заявку. По вопросам — напишите в поддержку.
              </div>
            </Card>
          ) : (
            <Note variant="danger">
              <div className="text-[15px] font-medium">Заявка отклонена</div>
              {current.cancel_reason && <div className="mt-2 text-[14px]">{current.cancel_reason}</div>}
              <div className="mt-2">Вы можете создать новую заявку. По вопросам — напишите в поддержку.</div>
            </Note>
          ))}

        {showTimeline && (
          <Section title="Статус заявки">
            <Card>
              {STEPS.map((s, i) => {
                const reached = i <= currentIdx;
                const active = i === currentIdx && !done && live;
                const isLast = i === STEPS.length - 1;
                return (
                  <div key={s.key} className="relative flex items-center gap-3.5 pb-6 last:pb-0">
                    {!isLast && (
                      <span
                        className={`absolute left-[13px] top-7 h-full w-0.5 ${i < currentIdx ? "bg-brand/50" : "bg-white/10"}`}
                      />
                    )}
                    <div
                      className={`z-10 grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-semibold ${
                        reached ? "bg-brand text-surface-base" : "bg-white/10 text-white/40"
                      }`}
                    >
                      {reached && !active ? "✓" : i + 1}
                    </div>
                    <div className={`flex flex-1 items-center gap-2 text-[15px] ${reached ? "" : "text-white/40"}`}>
                      <span className={active ? "font-medium" : ""}>{s.label}</span>
                      {active && <Spinner className="h-4 w-4" />}
                    </div>
                  </div>
                );
              })}
            </Card>
          </Section>
        )}

        <Section title="Детали заявки">
          <Card>
            <Row label="Заявка" value={current.reference} mono />
            <Row label="Создана" value={createdAt} />
            <Row label="Сумма пополнения" value={formatRub(current.amount_rub)} />
          </Card>
        </Section>

        {canCancel &&
          (confirming ? (
            <Card>
              <div className="text-[14px]">Отменить заявку? Действие необратимо.</div>
              <div className="mt-3 flex gap-2">
                <button
                  disabled={cancelling}
                  onClick={doCancel}
                  className="flex-1 rounded-xl bg-red-500/15 px-4 py-3 text-[15px] text-red-300 disabled:opacity-50"
                >
                  {cancelling ? "Отмена…" : "Да, отменить"}
                </button>
                <button
                  disabled={cancelling}
                  onClick={() => setConfirming(false)}
                  className="flex-1 rounded-xl bg-white/[0.06] px-4 py-3 text-[15px]"
                >
                  Нет
                </button>
              </div>
            </Card>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="w-full py-2 text-center text-[14px] text-red-300 active:opacity-70"
            >
              Отменить заявку
            </button>
          ))}
      </div>

      <FallbackButton text={done ? "Готово" : "В кабинет"} onClick={onDone} />
    </Screen>
  );
}
